// Malaysian phone number validation
export const validateMalaysianPhone = (phone) => {
    if (!phone) return "Phone number is required";

    // Remove all non-digit characters
    const cleanPhone = phone.replace(/\D/g, '');

    // Check length first
    if (cleanPhone.length < 9 || cleanPhone.length > 11) {
        return "Phone number must be 9-11 digits";
    }

    // Malaysian phone number validation logic
    const isValid = (() => {
        // Mobile numbers starting with 01 (9-10 digits)
        if (cleanPhone.startsWith('01') && (cleanPhone.length === 9 || cleanPhone.length === 10)) {
            const secondDigit = cleanPhone.charAt(2);
            // Valid mobile prefixes: 010, 011, 012, 013, 014, 016, 017, 018, 019
            return ['0', '1', '2', '3', '4', '6', '7', '8', '9'].includes(secondDigit);
        }

        // Mobile numbers starting with 1 (10-11 digits)
        if (cleanPhone.startsWith('1') && (cleanPhone.length === 10 || cleanPhone.length === 11)) {
            return true;
        }

        // Landline numbers starting with 0 (10 digits)
        if (cleanPhone.startsWith('0') && cleanPhone.length === 10) {
            const secondDigit = cleanPhone.charAt(1);
            // Valid landline prefixes: 03, 04, 05, 06, 07, 08, 09
            return ['3', '4', '5', '6', '7', '8', '9'].includes(secondDigit);
        }

        return false;
    })();

    if (!isValid) {
        return "Please enter a valid Malaysian phone number";
    }

    return null;
};

// E.164 phone number validation (Global Standard)
// Format: +<CountryCode><NationalNumber>
// Rules: Starts with +, Country code 1-3 digits, Total digits (excluding +): 8 to 15, No spaces/dashes/brackets
export const validateE164Phone = (phone) => {
    if (!phone) return "Phone number is required";

    const trimmedPhone = phone.trim();
    
    // Must start with +
    if (!trimmedPhone.startsWith('+')) {
        return "Phone number must start with + (E.164 format: +CountryCodeNumber)";
    }
    
    // Remove + and check if remaining are all digits
    const digitsOnly = trimmedPhone.substring(1);
    
    // Check for spaces, dashes, or brackets
    if (/[\s\-()\[\]]/.test(trimmedPhone)) {
        return "Phone number cannot contain spaces, dashes, or brackets (E.164 format)";
    }
    
    // Check if all characters after + are digits
    if (!/^\d+$/.test(digitsOnly)) {
        return "Phone number can only contain digits after the + sign";
    }
    
    // Check total digits (excluding +): 8 to 15 (Global E.164 Standard)
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
        return "Phone number must have 8-15 digits (E.164 format: +CountryCodeNumber)";
    }
    
    // Country code: 1-3 digits (this means the first 1-3 digits after +)
    // The total is already validated above, so we just need to ensure it's valid format
    // E.164 format is: +CCNNN... where CC is 1-3 digits and NNN... is the national number
    
    return null;
};

// Email validation
export const validateEmail = (email) => {
    if (!email) return "Email is required";

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return "Please enter a valid email address";
    }

    return null;
};

// Required field validation
export const validateRequired = (value, fieldName) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
        return `${fieldName} is required`;
    }
    return null;
};

// Name validation (letters, spaces, hyphens, apostrophes only)
export const validateName = (name, fieldName) => {
    const required = validateRequired(name, fieldName);
    if (required) return required;

    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name.trim())) {
        return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    }

    if (name.trim().length < 2) {
        return `${fieldName} must be at least 2 characters long`;
    }

    return null;
};

// IC/MyKad validation (Malaysian)
export const validateMyKad = (ic) => {
    if (!ic) return "IC/MyKad number is required";

    // Remove all non-digit characters
    const cleanIC = ic.replace(/\D/g, '');

    if (cleanIC.length !== 12) {
        return "IC/MyKad number must be 12 digits";
    }

    // Check if it's a valid date
    const month = parseInt(cleanIC.substring(2, 4));
    const day = parseInt(cleanIC.substring(4, 6));

    if (month < 1 || month > 12) {
        return "Invalid month in IC/MyKad number";
    }

    if (day < 1 || day > 31) {
        return "Invalid day in IC/MyKad number";
    }

    return null;
};

// Generic passport number validation
// Intended for basic format validation, not verification of passport authenticity.
export const validatePassport = (passport) => {
    if (!passport || !passport.trim()) {
        return "Passport number is required";
    }

    const cleanPassport = passport.trim().toUpperCase();

    // Passport numbers generally use letters and numbers.
    if (!/^[A-Z0-9]+$/.test(cleanPassport)) {
        return "Passport number can only contain letters and numbers";
    }

    // Generic sanity range — avoid country-specific assumptions.
    if (cleanPassport.length < 6 || cleanPassport.length > 15) {
        return "Passport number must be 6-15 characters long";
    }

    return null;
};

// Age validation (must be 18+ for staff, any age for children)
export const validateAge = (birthDate, minAge = 0) => {
    if (!birthDate) return "Date of birth is required";

    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    if (age < minAge) {
        return `Age must be at least ${minAge} years old`;
    }

    if (age > 120) {
        return "Please enter a valid date of birth";
    }

    return null;
};

// Password validation
export const validatePassword = (password) => {
    if (!password) return "Password is required";

    if (password.length < 8) {
        return "Password must be at least 8 characters long";
    }

    if (!/(?=.*[a-z])/.test(password)) {
        return "Password must contain at least one lowercase letter";
    }

    if (!/(?=.*[A-Z])/.test(password)) {
        return "Password must contain at least one uppercase letter";
    }

    if (!/(?=.*\d)/.test(password)) {
        return "Password must contain at least one number";
    }

    return null;
};

// Confirm password validation
export const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) return "Please confirm your password";

    if (password !== confirmPassword) {
        return "Passwords do not match";
    }

    return null;
};

// Image file validation
export const validateImageFile = (file) => {
    if (!file) return null; // Optional field

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
        return "Please upload a valid image file (JPEG, PNG, or GIF)";
    }

    if (file.size > maxSize) {
        return "Image file size must be less than 5MB";
    }

    return null;
};

// Branch validation
export const validateBranch = (branchId) => {
    if (!branchId) return "Branch is required";
    return null;
};

// Designation validation
export const validateDesignation = (designation) => {
    if (!designation) return "Designation is required";

    if (designation.trim().length < 2) {
        return "Designation must be at least 2 characters long";
    }

    return null;
};

// Address validation
export const validateAddress = (address) => {
    if (!address) return "Address is required";

    if (address.trim().length < 10) {
        return "Address must be at least 10 characters long";
    }

    return null;
};

// Relationship validation
export const validateRelationship = (relationship) => {
    if (!relationship) return "Relationship is required";

    const validRelationships = [
        'FATHER', 'MOTHER', 'GUARDIAN', 'GRANDFATHER', 'GRANDMOTHER',
        'UNCLE', 'AUNT', 'BROTHER', 'SISTER', 'OTHER'
    ];

    if (!validRelationships.includes(relationship)) {
        return "Please select a valid relationship";
    }

    return null;
};

// Form validation helper
export const validateForm = (formData, validationRules) => {
    const errors = {};

    Object.keys(validationRules).forEach(field => {
        const value = formData[field];
        const rules = validationRules[field];

        for (const rule of rules) {
            const error = rule(value, field);
            if (error) {
                errors[field] = error;
                break; // Stop at first error for this field
            }
        }
    });

    return errors;
};

// Format Malaysian phone number for display
export const formatMalaysianPhone = (phone) => {
    if (!phone) return '';

    const cleanPhone = phone.replace(/\D/g, '');

    if (cleanPhone.length === 9) {
        // For 9-digit numbers like 012-451-686
        return `${cleanPhone.substring(0, 3)}-${cleanPhone.substring(3, 6)}-${cleanPhone.substring(6)}`;
    } else if (cleanPhone.length === 10) {
        // For 10-digit numbers like 010-123-4567
        return `${cleanPhone.substring(0, 3)}-${cleanPhone.substring(3, 6)}-${cleanPhone.substring(6)}`;
    } else if (cleanPhone.length === 11) {
        // For 11-digit numbers like 1-234-567-8901
        return `${cleanPhone.substring(0, 1)}-${cleanPhone.substring(1, 4)}-${cleanPhone.substring(4, 7)}-${cleanPhone.substring(7)}`;
    }

    return phone;
};

// Format MyKad for display
export const formatMyKad = (ic) => {
    if (!ic) return '';

    const cleanIC = ic.replace(/\D/g, '');

    if (cleanIC.length === 12) {
        return `${cleanIC.substring(0, 6)}-${cleanIC.substring(6, 8)}-${cleanIC.substring(8)}`;
    }

    return ic;
};

// Format IP address for display
export const formatIpAddress = (ip) => {
    if (!ip) return '';

    // Remove all non-digit and non-dot characters
    const cleanIP = ip.replace(/[^\d.]/g, '');

    // Split by dots and take only the first 4 parts
    const parts = cleanIP.split('.').slice(0, 4);

    // Ensure each part is a valid number (0-255)
    const formattedParts = parts.map(part => {
        const num = parseInt(part, 10);
        if (isNaN(num)) return '';
        return Math.min(255, Math.max(0, num)).toString();
    });

    // Join with dots
    return formattedParts.join('.');
};

// URL validation
export const validateUrl = (url) => {
    if (!url) return null; // Optional field

    const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

    if (!urlPattern.test(url)) {
        return "Please enter a valid URL (e.g., https://www.example.com)";
    }

    return null;
};
